import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
} from 'antd';
import { DeleteOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { CURRENCY_OPTIONS, errorText, fold } from './format';

const TYPES = ['flat', 'house', 'office', 'parking', 'land', 'other'];

const toForm = (p) => ({
  address: p.address,
  type: p.type,
  project: p.project?._id || null,
  currency: p.currency,
  months: p.months,
  jointPayment: p.jointPayment,
  contractFrom: p.contractFrom ? dayjs(p.contractFrom) : null,
  contractTo: p.contractTo ? dayjs(p.contractTo) : null,
  tenants: p.tenants,
  note: p.note,
  active: p.active,
  owners: p.owners.map((o) => ({ recipient: o.recipient, netPerMonth: o.netPerMonth })),
});

/**
 * A rented property: its owners with their share of the monthly rent, how
 * many months one calculation pays for, the contract and who lives there.
 */
export default function PropertyModal({ open, company, property, recipients, projects, onAddRecipient, onClose, onSaved }) {
  const { t } = useTranslation('taxReturns');
  const [form] = Form.useForm();
  const owners = Form.useWatch('owners', form) || [];
  const currency = Form.useWatch('currency', form) || 'EUR';

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      property ? toForm(property) : { type: 'flat', currency: 'EUR', months: 3, active: true, owners: [{}] }
    );
  }, [open, property, form]);

  const save = useMutation({
    mutationFn: (v) => {
      const data = {
        ...v,
        contractFrom: v.contractFrom ? v.contractFrom.format('YYYY-MM-DD') : null,
        contractTo: v.contractTo ? v.contractTo.format('YYYY-MM-DD') : null,
        project: v.project || null,
      };
      return property ? API.taxReturns.updateProperty(company, property._id, data) : API.taxReturns.createProperty(company, data);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => API.taxReturns.deleteProperty(company, property._id),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const options = recipients
    .filter((r) => r.active || owners.some((o) => o?.recipient === r._id))
    .map((r) => ({
      value: r._id,
      label: `${r.name}${r.idNumber ? ` · ${r.idNumber}` : ''}`,
      search: fold(`${r.name} ${r.idNumber}`),
    }));
  const err = save.error || remove.error;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      title={property ? property.address : t('people.addProperty')}
      footer={
        <Space>
          {property && (
            <Popconfirm title={t('people.deleteConfirm')} onConfirm={() => remove.mutate()}>
              <Button shape="round" danger icon={<DeleteOutlined />} loading={remove.isPending}>
                {t('people.delete')}
              </Button>
            </Popconfirm>
          )}
          <Button shape="round" onClick={onClose}>
            {t('people.cancel')}
          </Button>
          <Button shape="round" type="primary" loading={save.isPending} onClick={() => form.submit()}>
            {t('people.save')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item
          name="address"
          label={t('people.fields.address')}
          rules={[{ required: true, message: t('errors.property_address_required') }]}
        >
          <Input />
        </Form.Item>
        <Space wrap align="start">
          <Form.Item name="type" label={t('people.fields.type')}>
            <Select style={{ width: 170 }} options={TYPES.map((v) => ({ value: v, label: t(`people.types.${v}`) }))} />
          </Form.Item>
          <Form.Item name="project" label={t('fields.project')}>
            <Select
              allowClear
              style={{ width: 220 }}
              options={projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` }))}
            />
          </Form.Item>
          <Form.Item name="currency" label={t('calc.fields.currency')}>
            <Select style={{ width: 90 }} options={CURRENCY_OPTIONS} />
          </Form.Item>
          <Form.Item name="months" label={t('people.fields.months')} tooltip={t('people.helpText.months')}>
            <InputNumber style={{ width: 110 }} min={1} max={36} precision={0} />
          </Form.Item>
        </Space>

        <Divider orientation="left" plain>
          {t('people.fields.owners')}
        </Divider>
        <Form.List name="owners">
          {(fields, { add, remove: removeRow }) => (
            <>
              {fields.map(({ key, name }) => (
                <Space key={key} wrap align="start">
                  <Form.Item
                    name={[name, 'recipient']}
                    rules={[{ required: true, message: t('errors.property_owner_required') }]}
                  >
                    <Select
                      showSearch
                      style={{ width: 360 }}
                      placeholder={t('people.pickOwner')}
                      options={options}
                      filterOption={(input, o) => o.search.includes(fold(input))}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[name, 'netPerMonth']}
                    rules={[{ required: true, message: t('errors.property_owner_required') }]}
                  >
                    <InputNumber
                      style={{ width: 170 }}
                      min={0}
                      step={0.01}
                      decimalSeparator=","
                      addonAfter={currency}
                      placeholder={t('people.fields.netPerMonth')}
                    />
                  </Form.Item>
                  {fields.length > 1 && (
                    <Button shape="round" type="text" icon={<MinusCircleOutlined />} onClick={() => removeRow(name)} />
                  )}
                </Space>
              ))}
              <Space wrap>
                <Button shape="round" icon={<PlusOutlined />} onClick={() => add({})}>
                  {t('people.ownerAdd')}
                </Button>
                <Button shape="round" type="link" onClick={onAddRecipient}>
                  {t('people.addRecipient')}
                </Button>
              </Space>
            </>
          )}
        </Form.List>
        {/* Outside the list (inside it the name would be prefixed), and always mounted so the saved value is kept. */}
        <Form.Item name="jointPayment" valuePropName="checked" hidden={owners.length < 2} style={{ marginTop: 12 }}>
          <Checkbox>{t('people.fields.jointPayment')}</Checkbox>
        </Form.Item>

        <Divider orientation="left" plain>
          {t('people.fields.contract')}
        </Divider>
        <Space wrap align="start">
          <Form.Item name="contractFrom" label={t('people.fields.contractFrom')}>
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="contractTo" label={t('people.fields.contractTo')}>
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="active" label={t('people.fields.active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
        <Form.Item name="tenants" label={t('people.fields.tenants')}>
          <Input />
        </Form.Item>
        <Form.Item name="note" label={t('fields.note')}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
        </Form.Item>
      </Form>
      {err && <Alert type="error" showIcon message={errorText(t, err)} />}
    </Modal>
  );
}
